import { Event, useStore } from '@mirai/data-sources';
import { dateDiff, useLocale } from '@mirai/locale';
import { ServiceRates } from '@mirai/services';
import { Notification, Progress, styles, Theme, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import {
  CardHotel,
  Filters,
  Item,
  ModalCancellationInfo,
  ModalExtras,
  ModalItemInfo,
  ModalItemUrl,
  ModalMapView,
  ModalOfferInfo,
  ModalTwin,
} from './components';
import {
  filterItem,
  getCardImageHeight,
  getCheckoutParams,
  getFeatures,
  getFinderParams,
  getForwarderUrl,
  getNetPrice,
  isValidCart,
  select,
} from './helpers';
import { useRates } from './hooks';
import { Content } from './Rates.Content';
import { Footer as RatesFooter } from './Rates.Footer';
import { Header as RatesHeader } from './Rates.Header';
import { L10N } from './Rates.l10n';
import * as style from './Rates.module.css';
import { RoomSelector } from './Rates.RoomSelector';
import Skeleton from './Rates.Skeleton';
import { Unavailability } from './Rates.Unavailability';
import { ERROR } from '../../Core.constants';
import { fetchUrlParams, getRefundableFromItems, getSearchProps } from '../../helpers';
import { getSafeTrust } from '../../trust';
import { Cards, Footer } from '../__shared__';
import { Finder } from '../Finder';
import { Header } from '../Header';
import { EVENT, getOccupationTotals, METRICS_EVENTS, opacity, replaceUrlParams, SUBMIT_DELAY } from '../helpers';

const Rates = ({ testId, ...others }) => {
  const { isDesktop, isMobile, width } = useDevice();
  const { actions, device, fetch, multiRoom, refRoomSelector, responseError, state } = useRates();
  const { set, value: store } = useStore();
  const {
    club,
    currency,
    finder = {},
    forwarder,
    hotel = {},
    language,
    locale,
    session,
    skeleton = false,
    urlParams = {},
    variant,
  } = store;

  const { translate } = useLocale();
  const refItems = useRef({});
  const ratesMetricsSent = useRef(false);

  const [busy, setBusy] = useState(false);
  const [cancellationInfo, setCancellationInfo] = useState();
  const [extras, setExtras] = useState();
  const [extrasClosed, setExtrasClosed] = useState();
  const [filterBoard, setFilterBoard] = useState();
  const [filters, setFilters] = useState({});
  const [itemInfo, setItemInfo] = useState();
  const [itemUrl, setItemUrl] = useState();
  const [mapView, setMapView] = useState();
  const [offerInfo, setOfferInfo] = useState();
  const [twinSelected, setTwinSelected] = useState();

  const { calendar = [], occupation = [] } = finder;
  const { cart, checkoutDataSource, clubDiscount, dataSource, fetching, metaBoard, room, strictSearch } = state;
  const { setCart, setDataSource, setMetaBoard, setRoom, setStrictSearch } = actions;

  const trust = getSafeTrust(store);

  useEffect(() => {
    if (!skeleton) trust.record('stageEntered', { stage: 'rates' });
  }, [skeleton, trust]);

  useEffect(() => {
    if (skeleton || responseError || !dataSource || ratesMetricsSent.current) return;

    Event.publish(EVENT.METRICS, {
      id: METRICS_EVENTS.RATES_READY,
      multiRoom,
      variant,
      ...getSearchProps({
        store: { finder, currency, language, locale, urlParams },
        dataSource,
      }),
      is_refundable: refundable,
    });
    ratesMetricsSent.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource]);

  useEffect(() => {
    if (!filterBoard) return;

    setMetaBoard(filterBoard);
    scrollToRoom(filterBoard.roomId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBoard]);

  // -- handlers -------------------------------------------------------------------------------------------------------
  const handleFilters = (next = {}) => {
    getSafeTrust(store).record('journeyAction', { stage: 'rates', name: 'filtersChanged' });
    const { strictSearch: nextStrictSearch } = next;

    setCart([]);
    setRoom(undefined);
    setFilters(next);

    if (strictSearch !== nextStrictSearch) setStrictSearch(nextStrictSearch);
  };

  const handleFinder = (values = {}) => {
    setCart([]);
    setDataSource(undefined);
    setRoom();
    setMetaBoard();
    scrollTo(0);

    replaceUrlParams(getFinderParams({ ...values, hotel }));
    const { calendar: nextCalendar, occupation: nextOccupation, place: nextPlace, promocode: nextPromocode } = values;
    fetch({ nextCalendar, nextOccupation, nextPlace, nextPromocode });
  };

  const handleSelect = (board, others) => {
    const selected = cart[room || 0];
    getSafeTrust(store).record('journeyAction', { stage: 'rates', name: board ? 'roomSelected' : 'roomRemoved' });
    const nextCart = [...cart];
    nextCart[room || 0] = board ? { ...board, ...others, netPrice: getNetPrice(board) } : undefined;

    if (board) {
      if (!selected) scrollToRoom(board.roomId);

      Event.publish(EVENT.RATES_ADD_TO_CART, {
        event: EVENT.RATES_ADD_TO_CART,
        response: { board, cart: nextCart, ...dataSource },
      });

      Event.publish(EVENT.METRICS, {
        id: METRICS_EVENTS.RATES_INTERACTION,
        action: 'room_selected',
        result_position: itemsFiltered.findIndex(({ id } = {}) => id === board.roomId) + 1,
        loyalty_discount_active: !!session || clubDiscount,
      });
    }

    setCart(nextCart);
    setExtras();
    setMetaBoard(undefined);
    setTwinSelected();
    if (!multiRoom && !!board) handleSubmit(nextCart);

    if (multiRoom) {
      if (board && !selected && room < occupation.length - 1) return setRoom(room + 1);
      if (!board) set({ rates: undefined });
    }
  };

  const handleSubmit = async (cart) => {
    if (!checkoutDataSource || !isValidCart(cart)) return;

    const { currency: hotelCurrency, infantAge } = checkoutDataSource;
    const { extras = [] } =
      (await ServiceRates.extras({
        ...getCheckoutParams({
          cart,
          clubDiscount,
          currency,
          dataSource: checkoutDataSource,
          forwarder,
          urlParams: fetchUrlParams(location.search),
        }),
        ...getOccupationTotals({ infantAge, occupation }),
        clubDiscount: !!session || clubDiscount,
        device,
        hotelCurrency,
        locale,
        session,
      }).catch(() => {})) || {};

    const selectableExtras = extras.some(({ included } = {}) => !included);

    if (selectableExtras) {
      Event.publish(EVENT.METRICS, {
        id: METRICS_EVENTS.EXTRAS_INTERACTION,
        action: 'view',
      });
      setExtras(extras);
      setExtrasClosed();
    } else {
      handleCheckout(cart, extras);
    }
  };

  const handleCheckout = (cart = [], selectedExtras = []) => {
    if (!checkoutDataSource || !isValidCart(cart)) return;

    const { infantAge } = checkoutDataSource;
    getSafeTrust(store).record('selectionChanged', { ...store, dataSource: checkoutDataSource });
    setBusy(true);

    Event.publish(EVENT.METRICS, {
      id: METRICS_EVENTS.RATES_INTERACTION,
      action: 'rates_submit',
      has_extras: extras?.length > 0,
    });

    setTimeout(() => {
      setBusy(false);
      document.location.href = getForwarderUrl({
        cart,
        clubDiscount,
        currency,
        dataSource: checkoutDataSource,
        extras: selectedExtras,
        forwarder,
        meta: !!urlParams.rates,
        refundable,
        session,
        urlParams: { ...fetchUrlParams(location.search), device },
        variant,
        occupationTotals: JSON.stringify(getOccupationTotals({ infantAge, occupation })),
      });
    }, SUBMIT_DELAY);
  };

  const handleCloseExtras = () => {
    setExtras();
    !multiRoom && setExtrasClosed(true);
  };

  const handleTwin = (roomId) => {
    setFilterBoard();
    setMetaBoard();
    setTwinSelected(roomId);
  };

  const scrollTo = (value = 0) => window.scrollTo({ top: value, behavior: 'smooth' });

  const scrollToRoom = (roomId) => {
    const { top } = refItems?.current[roomId]?.getBoundingClientRect() || {};
    if (top) scrollTo(top + window.scrollY - (isMobile ? 65 : 16 * 5));
  };

  const { contentBackground } = Theme.get();
  const cardImageHeight = getCardImageHeight();

  const { days: nights } = dateDiff(calendar[0], calendar[1]);
  const response = dataSource !== undefined;
  const roomSelectorProps = { cart, occupation, room, onPress: setRoom };
  const features = getFeatures(hotel.features, urlParams, { isDesktop });
  const priceFactor = features.showPricePerNight && nights > 1 ? nights : 1;
  const uniqueOption = (dataSource?.items || [])
    .filter(({ soldOut } = {}) => !soldOut)
    .every(
      ({ boards = [], cancellations = [], rates = [] }) =>
        boards.length === 1 && cancellations.length === 1 && rates.length === 1,
    );
  const itemsFiltered = select(dataSource?.items, filters, { priceFactor });
  const refundable = getRefundableFromItems(dataSource?.items);

  return (
    <>
      <Header currency login={!!club} name={features.showNameInHeader} skeleton={skeleton} />

      <View
        ref={refRoomSelector}
        className={styles(style.finder, style.blur, (!multiRoom || responseError) && style.sticky)}
        style={!isMobile ? { backgroundColor: opacity(contentBackground, 0.66) } : undefined}
      >
        <View row={isMobile} className={style.wrapper}>
          <Finder
            compacted={!isDesktop}
            icon
            onSubmit={handleFinder}
            testId={testId ? `${testId}-finder` : undefined}
          />

          {!responseError && dataSource?.items && (
            <Filters {...dataSource} {...{ clubDiscount, values: filters, priceFactor }} onSubmit={handleFilters} />
          )}
        </View>
      </View>

      <Progress indeterminate visible={fetching && !responseError} className={style.progress} />

      {isMobile && !responseError && (
        <RoomSelector {...roomSelectorProps} testId={testId ? `${testId}-multiroom` : undefined} />
      )}

      <View
        role="rates"
        data-trust-stage="rates"
        data-trust-scope={getSafeTrust(store).scope}
        testId={testId}
        className={styles(style.container, others.className)}
      >
        <View className={[style.wrapper, style.content]}>
          {!responseError && <Content {...dataSource} {...others} skeleton={skeleton} />}

          {(response || (multiRoom && !responseError)) && (
            <RatesHeader
              {...{ dataSource, filters, multiRoom, priceFactor, onMapView: setMapView, onSelect: handleSelect }}
            >
              {!isMobile ? (
                <RoomSelector {...roomSelectorProps} testId={testId ? `${testId}-multiroom` : undefined} />
              ) : undefined}
            </RatesHeader>
          )}

          {response ? (
            <Cards>
              {itemsFiltered?.map(({ id, ...item }, index) => {
                return (
                  <Item
                    {...{
                      ...filterItem(item, filters, priceFactor),
                      cardImageHeight,
                      cart,
                      extrasClosed,
                      features,
                      filters,
                      metaBoard,
                      id,
                      resultPosition: index + 1,
                      multiRoom,
                      nights,
                      priceFactor,
                      room,
                      taxesType: dataSource.taxesType,
                      uniqueOption,
                    }}
                    key={`${id || index}:${fetching}:${width}`}
                    ref={(el) => (refItems.current[id] = el)}
                    selected={cart[room || 0]}
                    onCancellationInfo={(rate) => {
                      getSafeTrust(store).record('journeyAction', { stage: 'rates', name: 'cancellationInfo' });
                      setCancellationInfo({ ...rate, name: item.name });
                    }}
                    onMoreInfo={() => {
                      getSafeTrust(store).record('journeyAction', { stage: 'rates', name: 'roomInfo' });
                      setItemInfo(item);
                    }}
                    onOfferInfo={(rate) => {
                      getSafeTrust(store).record('journeyAction', { stage: 'rates', name: 'offerInfo' });
                      setOfferInfo(rate);
                    }}
                    onSelect={handleSelect}
                    onTwin={handleTwin}
                    onViewUrl={(info) => {
                      getSafeTrust(store).record('journeyAction', { stage: 'rates', name: 'mediaViewed' });
                      setItemUrl(info);
                    }}
                    testId={testId ? `${testId}-item-${index}` : undefined}
                  />
                );
              })}

              {dataSource.hotels?.map((item = {}) => (
                <CardHotel key={item.id} {...{ ...item, cardImageHeight, nights, priceFactor }} />
              ))}
            </Cards>
          ) : responseError ? (
            responseError.hotel ||
            responseError.tracking?.hotelInfoBAR?.length === 0 ||
            responseError.tracking?.hotelInfoBAR[0].noAvailableReason ? (
              <Unavailability {...{ ...responseError, nights, priceFactor }} onMapView={setMapView} />
            ) : (
              <Notification error large className={style.error}>
                {translate(L10N.NOTIFICATION_ERROR[responseError.code || ERROR.UNKNOWN])}
              </Notification>
            )
          ) : (
            <Skeleton multiRoom={multiRoom} />
          )}
        </View>

        <RatesFooter
          {...{ cart: multiRoom ? cart : [], dataSource: checkoutDataSource, extras, multiRoom, occupation }}
          onSubmit={() => (multiRoom ? handleSubmit(cart) : undefined)}
          testId={testId ? `${testId}-footer` : undefined}
        />
      </View>

      {response && (
        <>
          <ModalItemInfo
            {...{ ...itemInfo, features, room }}
            visible={!!itemInfo}
            onClose={() => setItemInfo()}
            onViewUrl={(info) => {
              getSafeTrust(store).record('journeyAction', { stage: 'rates', name: 'mediaViewed' });
              setItemUrl(info);
            }}
          />
          <ModalCancellationInfo
            {...cancellationInfo}
            visible={!!cancellationInfo}
            onClose={() => setCancellationInfo()}
          />
          <ModalItemUrl {...itemUrl} visible={!!itemUrl} onClose={() => setItemUrl()} />
          <ModalOfferInfo {...{ ...offerInfo }} visible={!!offerInfo} onClose={() => setOfferInfo()} />
          <ModalTwin
            {...{
              ...itemInfo,
              clubDiscount,
              dataSource,
              cart,
              features,
              filters,
              nights,
              priceFactor,
              room,
              selected: twinSelected,
            }}
            onBook={handleSelect}
            onClose={() => setTwinSelected()}
            onFilter={handleFilters}
            onSelect={setFilterBoard}
          />
          <ModalExtras
            {...{ ...dataSource, busy, cart, extras }}
            visible={!!extras}
            onClose={handleCloseExtras}
            onSubmit={(extras) => handleCheckout(cart, extras)}
            testId={testId ? `${testId}-modalExtras` : undefined}
          />
        </>
      )}

      <ModalMapView items={mapView} nights={nights} visible={!!mapView} onClose={() => setMapView()} />

      <Footer />
    </>
  );
};

Rates.displayName = 'Mirai:Core:Rates';

Rates.propTypes = {
  testId: PropTypes.string,
};

export { Rates };
